/**
 * What to call a campaign on screen.
 *
 * <h3>Why this is not just `c.name`</h3>
 * The two campaign kinds spell it differently and always have: a WhatsApp
 * campaign carries `name`, a voice campaign carries `campaignName` (its table
 * column is `campaign_name`). A screen that shows both — the reply rules — read
 * `c.name` for both, so every voice campaign in its dropdown rendered as a bare
 * uuid. Nothing failed: the fallback did exactly what it was written to do.
 *
 * Reading EITHER spelling is what makes this safe to hand any campaign, so the
 * mistake cannot be made again in the other direction.
 */

const real = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  // "null"/"undefined" arrive from anything that stringifies its own nulls.
  if (!t || t.toLowerCase() === 'null' || t.toLowerCase() === 'undefined') return null;
  return t;
};

/**
 * A campaign's display name, whichever field it uses.
 *
 * Falls back to a short id — not useless, since the id is what a rule stores and
 * what the API filters on, but nobody recognises a campaign by uuid, so a rule
 * reading "on b43f60d1…" cannot be checked at a glance against the campaign
 * somebody actually sent.
 */
export const campaignLabel = (c: any): string => {
  const name = real(c?.name) || real(c?.campaignName);
  if (name) return name;
  const id = real(c?.id);
  return id ? id.slice(0, 8) : '(unnamed)';
};

/** A lookup by id over a mixed list, for rendering a stored campaignId. */
export const campaignLabelById = (list: any[]) => {
  const m = new Map<string, string>();
  (list || []).forEach((c) => { if (real(c?.id)) m.set(String(c.id), campaignLabel(c)); });
  return (id?: string | null): string | null => {
    if (!id) return null;
    // An id that is not in the list is shown short rather than blank: the
    // campaign may be archived, or on a page the list did not fetch.
    return m.get(id) || id.slice(0, 8);
  };
};
